import DocumentPage from './DocumentPage';

export const payrollDocument = {
  slug: 'payroll',
  route: '/payroll',
  title: 'Payroll',
  category: 'People & Operations',
  format: 'DOCX',
  description:
    'The Payroll module in EduX ERP manages contracts, salary attachments, payslips, batch payroll runs, reports, and payroll configuration for structured salary processing.',
  metrics: [
    { label: 'Primary focus', value: 'Salary processing' },
    { label: 'Core records', value: 'Contracts and payslips' },
    { label: 'Governance', value: 'Rules and structures' },
  ],
  workflow: [
    'Maintain contracts and salary attachments before payroll processing starts.',
    'Generate employee payslips or payslip batches for the payroll period and move them through approval states.',
    'Review payroll reports and configure salary structures, rules, work entries, and schedules for ongoing payroll control.',
  ],
  sections: [
    {
      id: 'introduction',
      title: 'Introduction',
      body: [
        'The Payroll module in EduX ERP is used to manage employee salaries, contracts, payslips, and salary structures.',
        'It ensures accurate salary computation based on attendance, leaves, allowances, and deductions.',
      ],
    },
    {
      id: 'contracts-and-attachments',
      title: 'Contracts and Salary Attachments',
      body: [
        'Go to Payroll -> Contract -> Contract List to manage all employee contract-related information.',
        'The contract list is used to create and manage employee salary agreements, while salary attachments manage employee salary deductions ordered by legal authorities.',
      ],
      gallery: [
        {
          src: '/assets/doc-images/payroll/image.png',
          alt: 'Payroll contract form with lifecycle status',
          caption: 'Contract form showing employee, schedule, wage details, and lifecycle states.',
        },
        {
          src: '/assets/doc-images/payroll/image2.png',
          alt: 'Contract allowances and deductions section',
          caption: 'Contract salary detail section covering allowances and deductions.',
        },
        {
          src: '/assets/doc-images/payroll/image3.png',
          alt: 'Salary attachment record for employee deduction',
          caption: 'Salary Attachment record used for recurring salary deductions.',
        },
      ],
      detailCards: [
        {
          title: 'Contract lifecycle',
          bullets: [
            'New: contract is created but not yet started.',
            'Running: contract is active and currently in use.',
            'Expired: contract has ended after reaching its end date.',
            'Cancelled: contract is manually terminated before its end date.',
          ],
        },
        {
          title: 'Salary attachment purpose and lifecycle',
          bullets: [
            'Used to deduct fixed amounts from salary.',
            'New: attachment is created but not yet applied to payslip.',
            'Running: deductions are active and applied regularly.',
            'Expired: the attachment has ended after completing its duration or amount.',
            'Cancelled: the attachment is manually stopped before completion.',
          ],
        },
      ],
    },
    {
      id: 'employee-payslips',
      title: 'Employee Payslips',
      body: [
        'Employee Payslip is used to generate and view an employee salary details, including earnings, deductions, and net salary for a specific period.',
        'The Employee Payslip consists of four states that represent its processing stages: Draft, Waiting, Done, and Rejected.',
      ],
      gallery: [
        {
          src: '/assets/doc-images/payroll/image4.png',
          alt: 'Employee payslip in draft state',
          caption: 'Draft payslip before computation and approval.',
        },
        {
          src: '/assets/doc-images/payroll/image5.png',
          alt: 'Employee payslip in waiting state',
          caption: 'Computed payslip waiting for confirmation.',
        },
        {
          src: '/assets/doc-images/payroll/image6.png',
          alt: 'Employee payslip in done state',
          caption: 'Finalized payslip in Done state.',
        },
      ],
      detailCards: [
        {
          title: 'Payslip states',
          bullets: [
            'Draft: payslip is created but not yet computed.',
            'Waiting: payslip is computed and waiting for approval.',
            'Done: payslip is confirmed and finalized.',
            'Rejected: payslip is cancelled or rejected and will not be processed.',
          ],
        },
      ],
    },
    {
      id: 'batches-and-reporting',
      title: 'Payslip Batches and Payroll Report',
      body: [
        'Payslips Batches are used to generate and manage multiple employee payslips at once for a specific payroll period, making bulk salary processing faster and more efficient.',
        'Payroll Report is used to view the summary of employee salaries, including total employees and total wages for a specific period.',
      ],
      gallery: [
        {
          src: '/assets/doc-images/payroll/image7.png',
          alt: 'Payslip batch for monthly payroll run',
          caption: 'Payslip batch screen used to generate payroll for multiple employees.',
        },
        {
          src: '/assets/doc-images/payroll/image8.png',
          alt: 'Payroll report summary screen',
          caption: 'Payroll report showing report date, total employees, and total wages.',
        },
      ],
    },
    {
      id: 'configuration',
      title: 'Configuration',
      body: [
        'The Configuration menu in the Payroll module is used to define salary structures, rules, work entries, and other settings required for payroll processing.',
      ],
      figure: {
        src: '/assets/doc-images/payroll/image9.png',
        alt: 'Payroll configuration menu',
        caption: 'Payroll configuration menu with salary structures, rules, registers, and other settings.',
      },
      subsections: [
        {
          id: 'settings',
          title: 'Settings',
          body: [
            'Settings are used to configure general payroll options.',
          ],
          figure: {
            src: '/assets/doc-images/payroll/imagea.png',
            alt: 'Payroll settings page',
            caption: 'Payroll settings page for accounting and salary processing options.',
          },
        },
        {
          id: 'salary-structures',
          title: 'Salary Structures',
          body: [
            'Salary Structure defines the rules and settings used to calculate employee salaries.',
          ],
          figure: {
            src: '/assets/doc-images/payroll/imageb.png',
            alt: 'Salary structure record',
            caption: 'Salary Structure form used for payroll calculation rules.',
          },
        },
        {
          id: 'unpaid-work-entry-types',
          title: 'Unpaid Work Entry Types',
          body: [
            'Unpaid Work Entry Types define non-paid attendance that reduces employee salary.',
          ],
          figure: {
            src: '/assets/doc-images/payroll/imagec.png',
            alt: 'Unpaid work entry type record',
            caption: 'Unpaid Work Entry Type used to reduce payable salary.',
          },
        },
        {
          id: 'salary-rule-categories',
          title: 'Salary Rule Categories',
          body: [
            'Salary Rule Categories are used to group similar salary rules together for better organization and structured salary computation.',
          ],
          figure: {
            src: '/assets/doc-images/payroll/imaged.png',
            alt: 'Salary rule category list',
            caption: 'Salary Rule Categories list used for organized payroll grouping.',
          },
        },
        {
          id: 'salary-rules',
          title: 'Salary Rules',
          body: [
            'Salary Rules define how each component of salary is calculated based on predefined conditions and formulas.',
          ],
          figure: {
            src: '/assets/doc-images/payroll/imagee.png',
            alt: 'Salary rules list',
            caption: 'Salary Rules list showing code, category, and contribution register.',
          },
        },
        {
          id: 'contribution-register',
          title: 'Contribution Register',
          body: [
            'Contribution Register is used to track statutory deductions like PF, ESI, and taxes.',
          ],
          figure: {
            src: '/assets/doc-images/payroll/imagef.png',
            alt: 'Contribution register list',
            caption: 'Contribution Register list for payroll deductions.',
          },
        },
        {
          id: 'salary-structure-type',
          title: 'Salary Structure Type',
          body: [
            'Salary Structure Type defines the category of employees and controls how their salary is processed, including wage type, payment schedule, and working hours.',
          ],
          figure: {
            src: '/assets/doc-images/payroll/image10.png',
            alt: 'Salary structure type record',
            caption: 'Salary Structure Type record defining schedule, wage type, and work entry behavior.',
          },
        },
        {
          id: 'other-input-types',
          title: 'Other Input Types',
          body: [
            'Other Input Types are used to add additional inputs in payslips.',
          ],
          figure: {
            src: '/assets/doc-images/payroll/image11.png',
            alt: 'Other input type record',
            caption: 'Other Input Type used for extra payroll inputs such as overtime.',
          },
        },
        {
          id: 'working-schedule',
          title: 'Working Schedule',
          body: [
            'Working Schedule defines employee working hours and days.',
          ],
          figure: {
            src: '/assets/doc-images/payroll/image12.png',
            alt: 'Working schedule list',
            caption: 'Working Schedule list showing configured working time definitions.',
          },
        },
      ],
    },
  ],
  related: ['hr', 'time-off', 'expenses'],
};

export default function PayrollPage() {
  return <DocumentPage document={payrollDocument} />;
}
